import {
  useEffect, useState,
} from 'react'
import {
  Check, Copy, Gift, Loader2, X,
} from 'lucide-react'
import {
  useReferralLink,
} from '../hooks/useReferralLink'
import {
  useReferralStatus,
} from '../hooks/useReferralStatus'
import {
  ReferralProgress,
} from './ReferralProgress'
import {
  OnboardingEvents,
  trackEvent,
} from '../analytics/posthog'
import {
  copyTextToClipboard,
} from '../utils/clipboard'

interface ReferralLinkModalProps {
  isOpen: boolean
  onClose: () => void
}

/**
 * Referral link spotlight — opened by the `?referral_link=1` deep link behind
 * the referral promo page's "Get your referral link" CTA. Visitors arrive on
 * the Integrations page with their personal link front and centre, instead of
 * having to spot the Copy button inside the ReferralStrip.
 */
export function ReferralLinkModal({ isOpen, onClose }: ReferralLinkModalProps) {
  // Same query key as ReferralStrip, so the page-level fetch is shared.
  const { data: link, isError: loadFailed } = useReferralLink({ enabled: isOpen })
  // Tiered reward + live progress; only loaded while the modal is open.
  const { status: referralStatus } = useReferralStatus({ enabled: isOpen })
  const [copied, setCopied] = useState(false)
  const [copyError, setCopyError] = useState(false)

  useEffect(() => {
    if (isOpen) {
      trackEvent(OnboardingEvents.REFERRAL_SPOTLIGHT_OPENED)
    }
  }, [isOpen])

  if (!isOpen) return null

  const handleCopy = async () => {
    if (!link) return
    try {
      await copyTextToClipboard(link.referral_url)
      setCopied(true)
      setCopyError(false)
      trackEvent(OnboardingEvents.REFERRAL_LINK_COPIED, { source: 'spotlight' })
      setTimeout(() => setCopied(false), 1600)
    } catch {
      setCopyError(true)
    }
  }

  const handleClose = () => {
    trackEvent(OnboardingEvents.REFERRAL_SPOTLIGHT_CLOSED)
    onClose()
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
        <button
          onClick={handleClose}
          aria-label="Close"
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 text-white flex items-center justify-center mb-4">
          <Gift className="w-6 h-6" />
        </div>

        <h2 className="text-xl font-bold text-gray-900">Your referral link</h2>

        {loadFailed
          ? (
            <p className="mt-2 text-sm text-gray-600">
              We couldn&apos;t fetch your link just now. It&apos;s also waiting for
              you on this page, under &ldquo;Who else needs Catch?&rdquo;
            </p>
          )
          : !link
              ? (
                <div className="mt-6 mb-2 flex justify-center">
                  <Loader2 className="w-6 h-6 text-primary-500 animate-spin" />
                </div>
              )
              : (
                <>
                  <p className="mt-2 text-sm text-gray-600">
                    Share it with the people who&apos;d love a hand with their admin.
                    Friends get {link.discount_label}, and everyone who sticks around
                    counts toward your reward.
                  </p>

                  <div className="mt-4 flex items-stretch gap-2">
                    <input
                      type="text"
                      readOnly
                      value={link.referral_url}
                      onFocus={(e) => e.currentTarget.select()}
                      aria-label="Your personal Catch referral link"
                      className="min-w-0 flex-1 px-3 py-2.5 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-700 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                    />
                    <button
                      onClick={() => void handleCopy()}
                      className="shrink-0 inline-flex items-center gap-1.5 bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-700 hover:to-primary-600 text-white text-sm font-medium px-4 py-2.5 rounded-lg shadow-soft transition-[background-color,box-shadow,transform] active:scale-[0.96]"
                    >
                      {copied
                        ? <Check className="w-4 h-4" />
                        : <Copy className="w-4 h-4" />}
                      {copied ? 'Copied!' : 'Copy'}
                    </button>
                  </div>

                  {copyError && (
                    <p className="mt-2 text-xs text-red-600">
                      Couldn&apos;t copy the link — long-press to copy it manually.
                    </p>
                  )}

                  {referralStatus && (
                    <ReferralProgress status={referralStatus} source="spotlight" />
                  )}

                  <p className="mt-3 text-xs text-gray-500">
                    Pro tip: ask your assistant to draft the invite — admin handled,
                    naturally.
                  </p>
                </>
              )}
      </div>
    </div>
  )
}
